import { z } from 'zod';
/**
 * Piezas comunes del contrato. Todo lo que viaja por el API usa estos tipos
 * base: identificadores UUID v4 en texto, marcas de tiempo ISO-8601 en UTC y
 * enumerados como cadena (SQLite no tiene enums nativos, ver DECISIONS.md).
 *
 * Los nombres de campo van en `camelCase` de punta a punta, igual que el resto
 * del contrato: solo las columnas de la base de datos son `snake_case`, vía
 * `@map` en `schema.prisma`.
 */
/** UUID v4 en texto. */
export const idSchema = z.uuid();
/** Marca de tiempo ISO-8601 **en UTC**, con `Z`. Nunca con desplazamiento. */
export const utcDateTimeSchema = z.iso.datetime({ offset: false });
/** Fecha sin hora, `YYYY-MM-DD`. Se usa para `dueDate`. */
export const dateOnlySchema = z.iso.date();
export const localeSchema = z.enum(['es', 'en']);
export const taskStatusSchema = z.enum(['todo', 'in_progress', 'done']);
export const taskSizeSchema = z.enum(['XS', 'S', 'M', 'L', 'XL']);
export const taskPrioritySchema = z.enum(['low', 'normal', 'high', 'urgent']);
export const activityTypeSchema = z.enum(['created', 'updated', 'commented', 'deleted', 'restored']);
/**
 * Paleta cerrada de 12 colores de proyecto.
 *
 * No están elegidos a ojo: son 12 tonos repartidos por la rueda de color, cada
 * uno ajustado a una luminancia distinta que forma una escalera. La escalera es
 * lo que hace que se distingan en escala de grises, y de paso resuelve el
 * daltonismo: una deficiencia de color confunde tonos, no luminancias, así que
 * dos proyectos nunca acaban siendo el mismo punto.
 *
 * Todos contrastan al menos 3:1 sobre blanco para servir de indicador no
 * textual. `backend/tests/palette.test.ts` comprueba las dos cosas con números,
 * así que si alguien retoca un color el test lo dirá.
 */
export const PROJECT_COLORS = [
    '#77131C', // rojo
    '#944208', // tostado
    '#5F4700', // ámbar oscuro
    '#4E7500', // lima
    '#064C1D', // verde bosque
    '#006B62', // verde azulado
    '#03557E', // azul océano
    '#3F66DD', // azul
    '#59289F', // violeta
    '#A422BF', // magenta
    '#992171', // rosa
    '#687684', // pizarra
];
export const projectColorSchema = z.enum(PROJECT_COLORS);
/** Formato único de error del API. `code` en SCREAMING_SNAKE para traducir. */
export const errorResponseSchema = z.object({
    error: z.object({
        code: z.string(),
        message: z.string(),
        details: z.record(z.string(), z.unknown()).optional(),
    }),
});
/** Parámetros de paginación por cursor. 50 elementos por página. */
export const cursorQuerySchema = z.object({
    cursor: z.string().optional(),
    limit: z.coerce.number().int().min(1).max(100).default(50),
});
/**
 * Envoltorio de una página de resultados. `nextCursor` a `null` significa que
 * no hay más elementos.
 */
export function pageSchema(item) {
    return z.object({
        items: z.array(item),
        nextCursor: z.string().nullable(),
    });
}
/** Marcas de tiempo que lleva toda entidad con borrado suave. */
export const softDeleteTimestampsSchema = z.object({
    createdAt: utcDateTimeSchema,
    updatedAt: utcDateTimeSchema,
    deletedAt: utcDateTimeSchema.nullable(),
});
/** Booleano tal y como llega en una query string (`?includeDeleted=true`). */
export const queryBooleanSchema = z.stringbool();
/** Parámetro de ruta `{id}`, el mismo en todos los recursos con `GET/PATCH/DELETE /recurso/{id}`. */
export const idParamsSchema = z.object({ id: idSchema });
//# sourceMappingURL=common.js.map